import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useQuery } from '@tanstack/react-query';
import Button from '../atoms/Button';
import Checkbox from '../atoms/Checkbox';
import CloseButton from '../atoms/CloseButton';
import NamedBox from '../atoms/NamedBox';
import { ToastType } from '../atoms/Toast';
import Dialog from '../molecules/Dialog';
import DashboardIcon from '../icons/DashboardIcon';
import gql from '../services/gql';
import { COLOR_BORDER, COLOR_DARK_GRAY, COLOR_TEXT } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';

interface BoardList {
  id: string;
  name: string;
  description?: string;
}

interface Board {
  id: string;
  name: string;
  lists: BoardList[];
}

interface BoardsResponse {
  boards?: Array<Board | null>;
}

interface PlayerListsResponse {
  lists?: Array<BoardList | null>;
}

interface ListOperationResponse {
  success?: string;
}

interface PlayerBoardDialogProps {
  open: boolean;
  onClose: () => void;
  playerId?: number;
  programId?: number;
  showToast: (message: string, type: ToastType) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 0,
  },
  paper: {
    width: '100%',
    maxWidth: 760,
    maxHeight: 'calc(100vh - 32px)',
    boxShadow: theme.shadows[10],
    outline: 'none',
    borderRadius: 8,
    overflow: 'hidden',
  },
  closeButton: {
    marginLeft: 'auto',
  },
  content: {
    padding: theme.spacing(2, 2.5, 2.5),
    maxHeight: 'calc(100vh - 136px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  actions: {
    position: 'sticky',
    bottom: theme.spacing(-2.5),
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(2, -2.5, 0),
    padding: theme.spacing(1.5, 2.5),
    background: '#fff',
    borderTop: `1px solid ${COLOR_BORDER}`,
  },
  saveButton: {
    minWidth: 112,
  },
  accordionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(1.25, 0),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
  },
  sectionCaret: {
    flexShrink: 0,
    width: 14,
    height: 14,
    color: COLOR_DARK_GRAY,
    transform: 'rotate(-90deg)',
    transition: 'transform 0.2s ease',
  },
  sectionCaretOpen: {
    transform: 'rotate(0deg)',
  },
  sectionTitle: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  sectionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0, 0, 2.25),
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  checklistText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_TEXT,
  },
  checklistLabel: {
    fontWeight: 700,
  },
  checklistDescription: {
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(12),
  },
  status: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_DARK_GRAY,
    padding: theme.spacing(1, 0),
  },
}), { name: PlayerBoardDialog.name });

function isSuccessfulOperation(response: ListOperationResponse | null | undefined) {
  const success = response?.success?.toLowerCase();
  return success === 'true' || success === '1' || success === 'success';
}

function getPlayerListIds(response: { getListsForPlayer: PlayerListsResponse } | undefined) {
  return (response?.getListsForPlayer?.lists || [])
    .filter(Boolean)
    .map(list => (list as BoardList).id);
}

export default function PlayerBoardDialog(props: PlayerBoardDialogProps) {
  const { open, onClose, playerId, programId, showToast } = props;
  const classes = useStyles();
  const [openBoardIds, setOpenBoardIds] = useState<string[]>([]);
  const [initialListIds, setInitialListIds] = useState<string[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const canLoad = open && !!playerId && !!programId;

  const boardsQuery = useQuery<{ getBoardsAndLists: BoardsResponse }>(
    ['getBoardsAndLists', programId],
    () => gql(`
      getBoardsAndLists(programId: ${programId}) {
        boards {
          id
          name
          lists {
            id
            name
            description
          }
        }
      }
    `),
    { enabled: canLoad, refetchOnWindowFocus: false },
  );

  const playerListsQuery = useQuery<{ getListsForPlayer: PlayerListsResponse }>(
    ['getListsForPlayer', programId, playerId],
    () => gql(`
      getListsForPlayer(programId: ${programId}, playerId: ${playerId}) {
        lists {
          id
        }
      }
    `),
    { enabled: canLoad, refetchOnWindowFocus: false },
  );

  const boards = (boardsQuery.data?.getBoardsAndLists?.boards || []).filter(Boolean) as Board[];
  const assignedListIds = getPlayerListIds(playerListsQuery.data);

  useEffect(() => {
    if (boards.length && !openBoardIds.length) {
      setOpenBoardIds(boards.map(board => board.id));
    }
  }, [boards, openBoardIds.length]);

  useEffect(() => {
    if (!open || isSaving) {
      return;
    }

    setInitialListIds(assignedListIds);
    setSelectedListIds(assignedListIds);
  }, [open, playerId, programId, playerListsQuery.data]);

  function toggleBoard(boardId: string) {
    setOpenBoardIds(currentIds => (
      currentIds.includes(boardId)
        ? currentIds.filter(id => id !== boardId)
        : [...currentIds, boardId]
    ));
  }

  function handleClose() {
    if (!isSaving) {
      onClose();
    }
  }

  function toggleList(listId: string, checked: boolean) {
    setSelectedListIds(currentIds => (
      checked
        ? [...currentIds, listId]
        : currentIds.filter(id => id !== listId)
    ));
  }

  async function updateListMembership(listId: string, operation: 'addPlayerToList' | 'removePlayerFromList') {
    const response = await gql(`
      mutation {
        ${operation}(programId: ${programId}, playerId: ${playerId}, listId: ${Number(listId)}) {
          success
        }
      }
    `) as Record<string, ListOperationResponse>;

    if (!isSuccessfulOperation(response[operation])) {
      throw new Error('The list update was not accepted.');
    }
  }

  async function saveChanges() {
    if (!programId || !playerId || isSaving) {
      return;
    }

    const initialListIdSet = new Set(initialListIds);
    const selectedListIdSet = new Set(selectedListIds);
    const listIdsToAdd = selectedListIds.filter(listId => !initialListIdSet.has(listId));
    const listIdsToRemove = initialListIds.filter(listId => !selectedListIdSet.has(listId));

    if (!listIdsToAdd.length && !listIdsToRemove.length) {
      onClose();
      return;
    }

    setIsSaving(true);

    try {
      const results = await Promise.all([
        ...listIdsToAdd.map(listId => updateListMembership(listId, 'addPlayerToList').then(() => true).catch(() => false)),
        ...listIdsToRemove.map(listId => updateListMembership(listId, 'removePlayerFromList').then(() => true).catch(() => false)),
      ]);

      if (results.every(Boolean)) {
        showToast('Player board updated.', ToastType.SUCCESS);
        onClose();
      } else {
        console.error('One or more player board updates failed.');
        const refreshedPlayerLists = await playerListsQuery.refetch();
        setInitialListIds(getPlayerListIds(refreshedPlayerLists.data));
        showToast('Unable to update all player board lists. Please try again.', ToastType.ERROR);
      }
    } catch (error) {
      console.error(error);
      showToast('Unable to update the player board. Please try again.', ToastType.ERROR);
    } finally {
      setIsSaving(false);
    }
  }

  const isLoading = boardsQuery.isLoading || playerListsQuery.isLoading;
  const hasError = boardsQuery.isError || playerListsQuery.isError;
  const hasChanges = initialListIds.some(listId => !selectedListIds.includes(listId))
    || selectedListIds.some(listId => !initialListIds.includes(listId));

  return (
    <Dialog
      className={classes.modal}
      contentClassName={classes.paper}
      open={open}
      title='Add player to board'
      noHeader={true}
      noContentPadding={true}
      onClose={handleClose}
    >
      <NamedBox
        headerContent={<CloseButton className={classes.closeButton} onClick={handleClose} />}
        name='Add player to board'
      >
        <div className={classes.content}>
          {isLoading ? (
            <div className={classes.status}>Loading boards...</div>
          ) : hasError ? (
            <div className={classes.status}>Unable to load boards.</div>
          ) : boards.length ? (
            <div className={classes.accordionList}>
              {boards.map(board => {
                const isOpen = openBoardIds.includes(board.id);

                return (
                  <div key={board.id} className={classes.section}>
                    <button type='button' className={classes.sectionHeader} onClick={() => toggleBoard(board.id)}>
                      <DashboardIcon className={clsx(classes.sectionCaret, isOpen && classes.sectionCaretOpen)} />
                      <span className={classes.sectionTitle}>{board.name}</span>
                    </button>

                    {isOpen && (
                      <div className={classes.sectionBody}>
                        {board.lists.length ? board.lists.map(list => (
                          <label key={list.id} className={classes.checklistItem}>
                            <Checkbox
                              checked={selectedListIds.includes(list.id)}
                              disabled={isSaving}
                              onChange={(checked: boolean) => toggleList(list.id, checked)}
                            />
                            <span className={classes.checklistText}>
                              <span className={classes.checklistLabel}>{list.name}</span>
                              {list.description && <span className={classes.checklistDescription}>{list.description}</span>}
                            </span>
                          </label>
                        )) : (
                          <div className={classes.status}>No lists in this board.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={classes.status}>No boards are available.</div>
          )}

          {!isLoading && !hasError && (
            <div className={classes.actions}>
              <Button
                primary
                className={classes.saveButton}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
                onClick={saveChanges}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </NamedBox>
    </Dialog>
  );
}